import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams();
  const navigate = useNavigate();

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该文章可能已被移除或链接无效</p>
            <Link
              to="/categories"
              className="inline-flex px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: 'mrds 官方平台',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类', href: '/categories' },
    { label: category?.name || '分类', href: `/categories?cat=${article.categoryId}` },
    { label: article.title },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://mrds.io/article/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="max-w-[800px] mx-auto px-6 md:px-10 py-8 md:py-12">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                {category?.name}
              </span>
              <span className="text-foreground-400 text-xs flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="text-foreground-400 text-xs flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-3 pb-6 border-b border-background-200/70">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 text-xs font-bold">M</span>
              </div>
              <div>
                <div className="text-foreground-900 text-sm font-medium">{article.author}</div>
                <div className="text-foreground-400 text-xs">mrds 官方平台</div>
              </div>
            </div>
          </div>

          <div className="relative h-[360px] md:h-[420px] rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="article-content prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-foreground-500 text-xs">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回
            </button>
            <div className="flex items-center gap-2">
              <button
                className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                aria-label="分享"
              >
                <i className="ri-share-line text-sm"></i>
              </button>
              <button
                className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                aria-label="收藏"
              >
                <i className="ri-bookmark-line text-sm"></i>
              </button>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}